import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance as PayrollIcon,
  Add as AddIcon,
  Description as PayslipIcon,
  Assessment as ReportIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import { Card } from '../../design-system';
import { EmptyState } from '../../design-system/components/core';
import MetricCard from '../../components/ui/MetricCard';

// Import stores
import usePayrollStore from '../../stores/payrollStore';
import useNotificationStore from '../../stores/notificationStore';

const PayrollDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { 
    payrollRuns, 
    payrollSummary,
    fetchPayrollRuns, 
    fetchPayrollSummary,
    isLoading 
  } = usePayrollStore();
  const { showError } = useNotificationStore();
  
  const [refreshing, setRefreshing] = useState(false);
  
  // Fetch data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  const loadDashboardData = async () => {
    try {
      setRefreshing(true);
      await Promise.all([
        fetchPayrollRuns({ limit: 5 }),
        fetchPayrollSummary({
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear()
        })
      ]);
    } catch (error) {
      showError('Failed to load payroll data');
    } finally {
      setRefreshing(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'processing':
        return 'info';
      case 'approved':
        return 'warning';
      case 'finalized':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };
  
  // Calculate metrics
  const metrics = payrollSummary ? [
    {
      title: 'Total Payroll',
      value: formatCurrency(payrollSummary.totalPayroll),
      icon: <MoneyIcon />,
      trend: payrollSummary.payrollTrend,
      color: theme.palette.primary.main
    },
    {
      title: 'Employees Paid',
      value: payrollSummary.employeesPaid || 0,
      icon: <PeopleIcon />,
      subtitle: `of ${payrollSummary.totalEmployees || 0} total`,
      color: theme.palette.success.main
    },
    {
      title: 'Average Salary',
      value: formatCurrency(payrollSummary.averageSalary),
      icon: <TrendingUpIcon />,
      trend: payrollSummary.salaryTrend,
      color: theme.palette.info.main
    },
    {
      title: 'Pending Approval',
      value: payrollSummary.pendingApproval || 0,
      icon: <CalendarIcon />,
      subtitle: 'payroll runs',
      color: theme.palette.warning.main
    }
  ] : [];
  
  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Payroll"
        subtitle="Manage employee salaries and payroll processing"
        icon={<PayrollIcon />}
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadDashboardData} disabled={refreshing}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/payroll/runs/new')}
            >
              New Payroll Run
            </Button>
          </Box>
        }
      />
      
      {/* Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>
      
      {/* Recent Payroll Runs */}
      <Card>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3 
          }}>
            <Typography variant="h6">Recent Payroll Runs</Typography>
            <Button
              size="small"
              onClick={() => navigate('/payroll/runs')}
            >
              View All
            </Button>
          </Box>
          
          {isLoading && !payrollRuns.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : payrollRuns.length === 0 ? (
            <EmptyState
              title="No Payroll Runs"
              description="Create your first payroll run to get started"
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/payroll/runs/new')}
                >
                  Create Payroll Run
                </Button>
              }
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {payrollRuns.slice(0, 5).map((run) => (
                <Card
                  key={run._id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                  onClick={() => navigate(`/payroll/runs/${run._id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {format(new Date(run.year, run.month - 1), 'MMMM yyyy')} Payroll
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Period: {format(new Date(run.periodStart), 'MMM d')} - {format(new Date(run.periodEnd), 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • {run.employeeCount} employees
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6">
                            {formatCurrency(run.totalAmount)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total Amount
                          </Typography>
                        </Box>
                        <Chip
                          label={run.status}
                          color={getStatusColor(run.status)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
            onClick={() => navigate('/payroll/payslips')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PayslipIcon color="primary" />
                </Box>
                <Box>
                  <Typography variant="h6">Payslips</Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage employee payslips
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
            onClick={() => navigate('/payroll/reports')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ReportIcon color="success" />
                </Box>
                <Box>
                  <Typography variant="h6">Reports</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate payroll reports and analytics
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
            onClick={() => navigate('/settings')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PayrollIcon color="info" />
                </Box>
                <Box>
                  <Typography variant="h6">Settings</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure payroll settings
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayrollDashboard;